/**
 * Shared detail-view drawer building blocks.
 *
 * Standard: ALL "View detail" interactions use OverlayDrawer.
 * Dialog is reserved for forms, confirmations, and actions only.
 *
 * Components:
 *   DetailDrawer          – wrapper (OverlayDrawer + header + body)
 *   DetailSection         – titled section with optional divider
 *   DetailField           – single label / value pair
 *   DetailMetadataGrid    – 2-column CSS grid of DetailField items
 *   DetailBadges          – flex row of badges with wrap
 */
import React from 'react';
import {
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Caption1,
  Text,
  Divider,
  Button,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import type { OverlayDrawerProps } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionTitle: {
    display: 'block',
  },
  metadataGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingVerticalS,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  fieldLabel: {
    color: tokens.colorNeutralForeground3,
  },
  badges: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
});

// ---------------------------------------------------------------------------
// DetailDrawer
// ---------------------------------------------------------------------------

export interface DetailDrawerProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  size?: OverlayDrawerProps['size'];
  children: React.ReactNode;
}

export const DetailDrawer: React.FC<DetailDrawerProps> = ({
  open,
  onClose,
  title,
  size = 'medium',
  children,
}) => {
  const styles = useStyles();

  return (
    <OverlayDrawer
      position="end"
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
      size={size}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              onClick={onClose}
              aria-label="Close"
            />
          }
        >
          {title}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={styles.body}>{children}</div>
      </DrawerBody>
    </OverlayDrawer>
  );
};

// ---------------------------------------------------------------------------
// DetailSection
// ---------------------------------------------------------------------------

export interface DetailSectionProps {
  title?: string;
  divider?: boolean;
  children: React.ReactNode;
}

export const DetailSection: React.FC<DetailSectionProps> = ({
  title,
  divider = false,
  children,
}) => {
  const styles = useStyles();

  return (
    <>
      {divider && <Divider />}
      <div className={styles.section}>
        {title && (
          <Text weight="semibold" className={styles.sectionTitle}>
            {title}
          </Text>
        )}
        {children}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// DetailField
// ---------------------------------------------------------------------------

export interface DetailFieldProps {
  label: string;
  children: React.ReactNode;
}

export const DetailField: React.FC<DetailFieldProps> = ({ label, children }) => {
  const styles = useStyles();

  return (
    <div className={styles.field}>
      <Caption1 className={styles.fieldLabel}>{label}</Caption1>
      {children}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DetailMetadataGrid
// ---------------------------------------------------------------------------

export interface DetailMetadataGridProps {
  children: React.ReactNode;
}

export const DetailMetadataGrid: React.FC<DetailMetadataGridProps> = ({ children }) => {
  const styles = useStyles();

  return <div className={styles.metadataGrid}>{children}</div>;
};

// ---------------------------------------------------------------------------
// DetailBadges
// ---------------------------------------------------------------------------

export interface DetailBadgesProps {
  children: React.ReactNode;
}

export const DetailBadges: React.FC<DetailBadgesProps> = ({ children }) => {
  const styles = useStyles();

  return <div className={styles.badges}>{children}</div>;
};
